function normalizeNumber(value) {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    typeof value === "boolean"
  ) {
    return null;
  }

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function coordinatePair(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  return [value.longitude ?? value.lng, value.latitude ?? value.lat];
}

export function normalizeCoordinate(value, { rejectZeroPair = false } = {}) {
  const pair = coordinatePair(value);
  if (!pair || pair.length < 2) return null;

  const longitude = normalizeNumber(pair[0]);
  const latitude = normalizeNumber(pair[1]);

  if (
    longitude === null ||
    latitude === null ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90 ||
    (rejectZeroPair && longitude === 0 && latitude === 0)
  ) {
    return null;
  }

  return [longitude, latitude];
}

export function createMapRecenterTarget(
  value,
  type,
  { rejectZeroPair = false } = {},
) {
  const coordinate = normalizeCoordinate(value, { rejectZeroPair });

  if (!coordinate) return null;

  return {
    lng: coordinate[0],
    lat: coordinate[1],
    type,
  };
}

export function getMainMapLayout({
  width = 0,
  height = 0,
  panelExpanded = false,
  panelWidth,
  openHeight,
  closedHeight,
}) {
  const isDesktop =
    width >= 1024 || (width >= 720 && width / Math.max(height, 1) >= 1.4);

  return {
    isDesktop,
    panelExpanded: isDesktop || panelExpanded,
    panelWidth: panelWidth || Math.min(width * 0.4, 416),
    openHeight,
    closedHeight,
  };
}

export function getMainMapPadding(layout, open = layout.panelExpanded) {
  if (layout.isDesktop) {
    return {
      top: 100,
      bottom: 80,
      // Desktop always shows the panel, including a fresh map with no selection.
      left: layout.panelWidth + 32,
      right: 80,
    };
  }

  return {
    top: 100,
    bottom: Math.max(
      96,
      open ? (layout.openHeight ?? 220) : (layout.closedHeight ?? 120),
    ),
    left: 80,
    right: 80,
  };
}

export function getMainMapInitialView({ coordinate, lastMapCenter, fallback }) {
  const selectedCoordinate = normalizeCoordinate(coordinate, {
    rejectZeroPair: true,
  });

  if (selectedCoordinate) {
    return {
      center: selectedCoordinate,
      zoom: Math.max(normalizeNumber(lastMapCenter?.zoom) || 0, 15.5),
    };
  }

  const storedCoordinate = normalizeCoordinate(lastMapCenter?.center);
  return storedCoordinate
    ? { center: storedCoordinate, zoom: lastMapCenter.zoom }
    : fallback;
}

export function getMainMapUrlSelection(query = {}, search = "") {
  const params = new URLSearchParams(search);
  const getValue = (key) => {
    if (params.has(key)) return params.get(key);
    const value = query[key];
    return Array.isArray(value) ? value[0] : value;
  };
  const coordinate = normalizeCoordinate([getValue("lng"), getValue("lat")], {
    rejectZeroPair: true,
  });

  return {
    coordinate,
    hasSelection: Boolean(
      getValue("stop") ||
      getValue("vehicle") ||
      getValue("scooter") ||
      getValue("pin") === "1",
    ),
  };
}

const cameraPaddingKeys = ["top", "bottom", "left", "right"];
const cameraNumberEqual = (first, second, tolerance = 0.01) =>
  Number.isFinite(first) &&
  Number.isFinite(second) &&
  Math.abs(first - second) <= tolerance;

function getMainMapCameraIntent(map, options = {}) {
  const mapCenter = map.getCenter();
  const center = normalizeCoordinate(options.center) || [
    mapCenter.lng,
    mapCenter.lat,
  ];
  const padding = options.padding || map.getPadding();

  return {
    center,
    zoom: normalizeNumber(options.zoom) ?? map.getZoom(),
    padding: Object.fromEntries(
      cameraPaddingKeys.map((key) => [key, Number(padding[key]) || 0]),
    ),
  };
}

export function mainMapCameraIntentsEqual(first, second) {
  return Boolean(
    first &&
    second &&
    cameraNumberEqual(first.center[0], second.center[0], 1e-9) &&
    cameraNumberEqual(first.center[1], second.center[1], 1e-9) &&
    cameraNumberEqual(first.zoom, second.zoom, 1e-6) &&
    cameraPaddingKeys.every((key) =>
      cameraNumberEqual(first.padding[key], second.padding[key]),
    ),
  );
}

export function createMainMapCameraController(
  map,
  { duration = 350, initialView } = {},
) {
  // Apply saved coordinates and drawer padding in one supported camera update,
  // before the page attaches its persistence and user-movement listeners.
  if (initialView) map.jumpTo(initialView);
  let activeIntent = getMainMapCameraIntent(map);
  let pendingRequest = null;
  let programmatic = false;

  const run = (options) => {
    const intent = getMainMapCameraIntent(map, options);
    activeIntent = intent;
    programmatic = true;
    map.easeTo({
      ...options,
      center: intent.center,
      zoom: intent.zoom,
      padding: intent.padding,
      duration: options.duration ?? duration,
    });
  };

  return {
    request(options) {
      const intent = getMainMapCameraIntent(map, options);
      const requestedIntent = pendingRequest?.intent || activeIntent;
      if (mainMapCameraIntentsEqual(requestedIntent, intent)) return false;

      if (programmatic && map.isMoving()) {
        pendingRequest = { intent, options };
        return true;
      }

      pendingRequest = null;
      run(options);
      return true;
    },
    handleMoveEnd() {
      activeIntent = getMainMapCameraIntent(map);
      programmatic = false;
      if (!pendingRequest) return false;

      const { intent, options } = pendingRequest;
      pendingRequest = null;
      if (mainMapCameraIntentsEqual(activeIntent, intent)) return false;
      run(options);
      return true;
    },
    handleUserMove() {
      pendingRequest = null;
      programmatic = false;
      activeIntent = getMainMapCameraIntent(map);
    },
    isProgrammatic() {
      return programmatic;
    },
  };
}

function normalizeBearing(value) {
  const bearing = normalizeNumber(value);
  return bearing !== null && bearing >= 0 && bearing <= 360 ? bearing : 0;
}

export function normalizeVehiclePosition(position) {
  if (!position || typeof position !== "object") return null;

  const coordinate = normalizeCoordinate(
    [position.longitude, position.latitude],
    { rejectZeroPair: true },
  );
  if (!coordinate) return null;

  const lastPositionReportedAt = normalizeNumber(
    position.lastPositionReportedAt,
  );

  return {
    longitude: coordinate[0],
    latitude: coordinate[1],
    bearing: normalizeBearing(position.bearing),
    ...(position.positionStale
      ? {
          positionStale: true,
          ...(lastPositionReportedAt && lastPositionReportedAt > 0
            ? { lastPositionReportedAt }
            : {}),
        }
      : {}),
  };
}

export function formatStalePositionAge(
  lastPositionReportedAt,
  now = Date.now(),
) {
  const timestamp = normalizeNumber(lastPositionReportedAt);
  const nowMs = now instanceof Date ? now.getTime() : normalizeNumber(now);

  if (timestamp === null || timestamp <= 0 || nowMs === null) return "recently";

  const ageSeconds = Math.max(0, nowMs / 1000 - timestamp);
  if (ageSeconds < 45) return "under a minute ago";
  if (ageSeconds < 90) return "about a minute ago";
  return "a few minutes ago";
}

export function isTripMapLiveUpdateEligible({
  isLiveServiceDayMatch,
  isLiveTimingPlausible,
  hasUpdate,
  hasProviderMessage,
}) {
  return Boolean(
    isLiveServiceDayMatch &&
    isLiveTimingPlausible &&
    hasUpdate &&
    !hasProviderMessage,
  );
}

function shapeCoordinate(point) {
  return normalizeCoordinate([point?.shape_pt_lon, point?.shape_pt_lat]);
}

function stopCoordinate(stop) {
  return normalizeCoordinate([stop?.stop_lon, stop?.stop_lat]);
}

function getValidRouteCoordinates(shape, stops) {
  const shapeCoordinates = (shape || []).map(shapeCoordinate).filter(Boolean);
  if (shapeCoordinates.length > 0) return shapeCoordinates;
  return (stops || []).map(stopCoordinate).filter(Boolean);
}

export function getTripRouteFallback({ shape, stops, regionalDefault }) {
  const coordinates = getValidRouteCoordinates(shape, stops);

  if (coordinates.length >= 2) {
    const longitudes = coordinates.map(([longitude]) => longitude);
    const latitudes = coordinates.map(([, latitude]) => latitude);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);

    if (east - west > 180) {
      throw new Error("Trip route crosses the antimeridian");
    }

    return {
      type: "bounds",
      bounds: [
        [west, Math.min(...latitudes)],
        [east, Math.max(...latitudes)],
      ],
    };
  }

  const center = coordinates[0] ||
    normalizeCoordinate(regionalDefault) || [174.7743, -41.2883];
  return { type: "center", center, zoom: 13 };
}

function normalizedVehicleCoordinate(vehiclePosition) {
  return vehiclePosition
    ? normalizeCoordinate(
        [vehiclePosition.longitude, vehiclePosition.latitude],
        { rejectZeroPair: true },
      )
    : null;
}

function routeFallbackCenter(routeFallback) {
  if (routeFallback?.type === "bounds") {
    const [southWest, northEast] = routeFallback.bounds;
    return [
      (southWest[0] + northEast[0]) / 2,
      (southWest[1] + northEast[1]) / 2,
    ];
  }

  return normalizeCoordinate(routeFallback?.center) || [174.7743, -41.2883];
}

export function getTripMapInitialView({
  vehiclePosition,
  trackingEnabled,
  userLocation,
  routeFallback,
}) {
  const vehicleCoordinate = normalizedVehicleCoordinate(vehiclePosition);
  if (vehicleCoordinate) return { center: vehicleCoordinate, zoom: 16 };

  const normalizedUserLocation = trackingEnabled
    ? normalizeCoordinate(userLocation, { rejectZeroPair: true })
    : null;
  if (normalizedUserLocation)
    return { center: normalizedUserLocation, zoom: 16 };

  return { center: routeFallbackCenter(routeFallback), zoom: 13 };
}

function coordinatesEqual(first, second) {
  return first[0] === second[0] && first[1] === second[1];
}

export function getTripMapTargetPitch({
  trackingEnabled = false,
  preferSatellite = false,
} = {}) {
  return preferSatellite || trackingEnabled ? 0 : 32;
}

export function getTripMapCameraIntent({
  mode = "follow",
  trackingEnabled = false,
  preferSatellite = false,
  vehiclePosition,
  userLocation,
  routeFallback,
}) {
  const targetPitch = getTripMapTargetPitch({
    trackingEnabled,
    preferSatellite,
  });
  if (mode === "manual") return { type: "preserve-center", targetPitch };

  const vehicleCoordinate = normalizedVehicleCoordinate(vehiclePosition);
  const normalizedUserLocation = trackingEnabled
    ? normalizeCoordinate(userLocation, { rejectZeroPair: true })
    : null;

  if (normalizedUserLocation && vehicleCoordinate) {
    if (coordinatesEqual(normalizedUserLocation, vehicleCoordinate)) {
      return {
        type: "center",
        center: vehicleCoordinate,
        zoom: 16,
        targetPitch,
      };
    }

    return {
      type: "bounds",
      bounds: [normalizedUserLocation, vehicleCoordinate],
      targetPitch,
    };
  }

  const dynamicCenter = normalizedUserLocation || vehicleCoordinate;
  if (dynamicCenter) {
    return { type: "center", center: dynamicCenter, zoom: 16, targetPitch };
  }

  if (routeFallback?.type === "bounds") {
    return { ...routeFallback, targetPitch };
  }

  return {
    type: "center",
    center: routeFallbackCenter(routeFallback),
    zoom: routeFallback?.zoom ?? 13,
    targetPitch,
  };
}
